import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  RESOURCE_GROUPS,
  RESOURCE_LABELS,
  toNumber,
  type ActivityOrganisationPeriod,
  type ActivityResourceTotal,
} from "./activityResources";
import { RelativeTime } from "./RelativeTime";

/**
 * One organisation's resources: what it created in the selected period beside its all-time count and the first
 * and last time it created each. Resources it has never created are left out.
 */
export function ResourceDetailTable({
  allTime,
  period,
  periodLabel,
  className,
}: {
  allTime: ActivityResourceTotal[];
  period: ActivityOrganisationPeriod | undefined;
  periodLabel: string;
  className?: string;
}) {
  const inPeriod = new Map((period?.counts ?? []).map((c) => [c.resource, c]));
  const byResource = new Map(allTime.map((t) => [t.resource, t]));
  const rows = RESOURCE_GROUPS.flatMap((g) => g.resources).filter((r) => byResource.has(r));

  if (rows.length === 0) {
    return <p className="text-muted-foreground py-4 text-center text-sm">Nothing created yet.</p>;
  }

  return (
    <Table className={cn("text-xs", className)}>
      <TableHeader>
        <TableRow>
          <TableHead>Resource</TableHead>
          <TableHead className="text-right">{periodLabel}</TableHead>
          <TableHead className="text-right">All time</TableHead>
          <TableHead>First created</TableHead>
          <TableHead>Last created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((resource) => {
          const total = byResource.get(resource)!;
          const thisPeriod = toNumber(inPeriod.get(resource)?.count);
          return (
            <TableRow key={resource}>
              <TableCell className="font-medium">{RESOURCE_LABELS[resource]}</TableCell>
              <TableCell className={cn("text-right tabular-nums", thisPeriod === 0 && "text-muted-foreground")}>
                {thisPeriod === 0 ? "-" : thisPeriod.toLocaleString("en-GB")}
              </TableCell>
              <TableCell className="text-right tabular-nums">{toNumber(total.count).toLocaleString("en-GB")}</TableCell>
              <TableCell>
                <RelativeTime value={total.firstCreatedAt} />
              </TableCell>
              <TableCell>
                <RelativeTime value={total.lastCreatedAt} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
